import { ListSkeleton, SectionCard } from '@atlas/ui-components'
import { Button, Chip } from '@atlas/ui-primitives'
import type { ReactNode } from 'react'
import { ConfirmDialog } from '@/components/confirm/ConfirmDialog'
import type { MatrixAddRowController } from './MatrixAddRow'
import { MatrixAddRow } from './MatrixAddRow'
import type { MatrixWriteCapabilities } from './MatrixGrid'
import { GrowGridButton, MatrixGrid } from './MatrixGrid'
import type { MatrixEditController } from './MatrixRowView'
import type { MatrixRow } from './matrix-model'

export type { MatrixWriteCapabilities }

const EVERY_WRITE: MatrixWriteCapabilities = { canAddMessage: true, canTranslate: true, canEditRows: true }

/** The bulk-translate trigger and whatever actions the embedding surface adds. Growing the grid happens at its edges, not here. */
function MatrixToolbar({
	onApplyTranslation,
	selectedCount,
	isApplying,
	capabilities,
	extraActions,
}: {
	onApplyTranslation: () => void
	selectedCount: number
	isApplying: boolean
	capabilities: MatrixWriteCapabilities
	extraActions?: ReactNode
}) {
	return (
		<div className="flex flex-wrap items-center gap-2">
			{extraActions}
			{capabilities.canTranslate ? (
				<Button className="rounded-ds-full" isDisabled={selectedCount === 0 || isApplying} onPress={onApplyTranslation}>
					{isApplying ? 'Queueing…' : `Apply AI translation · ${selectedCount}`}
				</Button>
			) : null}
		</div>
	)
}

/** Only the actions this surface actually offers are explained — a read-only grid keeps the chips and drops the how-to. */
function legendHint(capabilities: MatrixWriteCapabilities, hasAddLocaleColumn: boolean): string {
	return [
		capabilities.canTranslate &&
			'Tick single cells, a message row for every locale, or a locale column for every message, then Apply AI translation.',
		capabilities.canEditRows && "Edit changes a row's text in place.",
		capabilities.canAddMessage && 'The + below the last row adds a message.',
		hasAddLocaleColumn && 'The + past the last column adds a locale.',
	]
		.filter((hint): hint is string => hint !== false)
		.join(' ')
}

/** Loading, load failure, or nothing to show — at most one of the three, never the grid alongside them. */
function MatrixStatus({
	isLoading,
	isError,
	isEmpty,
	emptyLabel,
}: {
	isLoading: boolean
	isError: boolean
	isEmpty: boolean
	emptyLabel: string
}) {
	if (isLoading) return <ListSkeleton label="Loading…" />
	if (isError)
		return (
			<p role="alert" className="text-sm text-ds-danger">
				Unable to load messages.
			</p>
		)
	if (isEmpty) return <p className="text-sm text-ds-text-muted">{emptyLabel}</p>
	return null
}

function MatrixLegend({ hint }: { hint: string }) {
	return (
		<div className="mt-4 flex flex-wrap items-center gap-3 border-t border-ds-border pt-3 text-ds-sm text-ds-text-subtle">
			<Chip variant="success" size="sm">
				manual
			</Chip>
			<Chip variant="primary" size="sm">
				AI
			</Chip>
			<span>{hint}</span>
		</div>
	)
}

export interface MessageResolutionMatrixProps {
	rows: MatrixRow[]
	/** Locale codes, in column order. */
	localeColumns: string[]
	isLoading: boolean
	isError: boolean
	/** `buildRequestKey(sourceMessageId, locale)` keys of the ticked cells. */
	selection: ReadonlySet<string>
	selectedCount: number
	onToggleCell: (sourceMessageId: string, locale: string) => void
	/** Ticks every locale one message can be translated into. */
	onToggleRow: (row: MatrixRow) => void
	/** Ticks every listed message that can be translated into one locale. */
	onToggleLocale: (locale: string) => void
	availableLocaleOptions: { value: string; label: string }[]
	onAddLocale: (locale: string) => void
	onApplyTranslation: () => void
	/** Count of ticked cells that already hold text — non-null while the overwrite confirmation is pending. */
	pendingOverwrites?: number | null
	onConfirmApplyOverwrites?: () => void
	onCancelApplyOverwrites?: () => void
	isApplying: boolean
	batchError?: string | null
	edit: MatrixEditController
	add: MatrixAddRowController
	/** The section's own heading — an embedding surface names it for what it is there. */
	heading?: string
	/** What "nothing to show" means here: filters that match nothing, or a surface nothing is attached to yet. */
	emptyLabel?: string
	/** The embedding surface's own toolbar actions, alongside the grid's. */
	extraActions?: ReactNode
	/** The read hit its page ceiling, so the grid is a first slice rather than everything there is. */
	isTruncated?: boolean
	/** Defaults to every write, which is what the Messages page offers. */
	capabilities?: MatrixWriteCapabilities
}

/**
 * Every listed piece of copy against every locale it could ship in: rows are
 * translation groups, columns are locales, and each cell carries that locale's
 * text plus a chip saying where the text came from — `manual` for copy a person
 * wrote, `AI` for a machine translation. A locale with no copy at all shows as
 * an empty cell.
 *
 * Ticking cells across any number of rows and locales and pressing Apply AI
 * translation queues them in one call — the point of the grid, versus the detail
 * page's one-message-one-locale panel. The two whole-axis questions get one tick
 * each: the row's box takes one message into every locale, a column's box takes
 * every listed message into that locale. Purely presentational: the page's
 * `useMessageResolutionMatrix` owns the reads, the selection and the writes.
 *
 * The grid grows at its edges, spreadsheet-style: a plus past the last column
 * adds a locale, a plus below the last row opens the new-message draft. Both sit
 * on the axis they extend, so neither reads as a page-level action in the
 * toolbar, which keeps only bulk translation and the embedding surface's own.
 *
 * The same grid serves a surface that owns a subset of messages — a Campaign's
 * associated copy — through `heading`, `emptyLabel`, `extraActions` and
 * `capabilities`: it names the section for what it is there, and offers only the
 * writes that surface's own capabilities grant.
 */
export function MessageResolutionMatrix({
	rows,
	localeColumns,
	isLoading,
	isError,
	selection,
	selectedCount,
	onToggleCell,
	onToggleRow,
	onToggleLocale,
	availableLocaleOptions,
	onAddLocale,
	onApplyTranslation,
	pendingOverwrites = null,
	onConfirmApplyOverwrites,
	onCancelApplyOverwrites,
	isApplying,
	batchError,
	edit,
	add,
	heading = 'Message resolution matrix',
	emptyLabel = 'No messages match the current filters.',
	extraActions,
	isTruncated = false,
	capabilities = EVERY_WRITE,
}: MessageResolutionMatrixProps) {
	// A locale column is only ever a place to put copy - an empty one is a dead end
	// for a user who can neither translate into it nor type into it.
	const hasAddLocaleColumn =
		availableLocaleOptions.length > 0 && (capabilities.canTranslate || capabilities.canEditRows)
	const showAddMessage = capabilities.canAddMessage && !add.isOpen

	return (
		<SectionCard
			title={heading}
			description="text · manual / AI"
			action={
				<MatrixToolbar
					onApplyTranslation={onApplyTranslation}
					selectedCount={selectedCount}
					isApplying={isApplying}
					capabilities={capabilities}
					extraActions={extraActions}
				/>
			}
		>
			{batchError ? (
				<p role="alert" className="mb-3 text-ds-sm text-ds-danger">
					{batchError}
				</p>
			) : null}

			{/* Exclusive with the grid: a refetch keeps the cached rows on screen, and
			    "Loading…" stacked over a populated grid says nothing the rows do not. */}
			{rows.length === 0 ? (
				<MatrixStatus isLoading={isLoading} isError={isError} isEmpty emptyLabel={emptyLabel} />
			) : null}

			{isTruncated ? (
				<p className="mb-3 text-ds-sm text-ds-text-subtle">
					Showing the first {rows.length} messages. Narrow the filters to reach the rest.
				</p>
			) : null}

			{rows.length > 0 ? (
				<MatrixGrid
					rows={rows}
					localeColumns={localeColumns}
					selection={selection}
					capabilities={capabilities}
					hasAddLocaleColumn={hasAddLocaleColumn}
					availableLocaleOptions={availableLocaleOptions}
					onAddLocale={onAddLocale}
					onToggleCell={onToggleCell}
					onToggleRow={onToggleRow}
					onToggleLocale={onToggleLocale}
					edit={edit}
					onOpenAddMessage={showAddMessage ? add.open : null}
				/>
			) : null}

			{/* Nothing listed yet still has to be growable, and with no grid there is no last row to grow from. */}
			{rows.length === 0 && showAddMessage ? (
				<div className="mt-3">
					<GrowGridButton label="Add message" onPress={add.open} />
				</div>
			) : null}

			{add.isOpen ? <MatrixAddRow add={add} /> : null}

			{rows.length > 0 ? <MatrixLegend hint={legendHint(capabilities, hasAddLocaleColumn)} /> : null}

			<OverwriteConfirmDialog
				count={pendingOverwrites}
				onConfirm={onConfirmApplyOverwrites}
				onCancel={onCancelApplyOverwrites}
			/>
		</SectionCard>
	)
}

/** The batch's overwrite gate: names how many filled cells the apply would replace before it runs. */
function OverwriteConfirmDialog({
	count,
	onConfirm,
	onCancel,
}: {
	count: number | null
	onConfirm?: () => void
	onCancel?: () => void
}) {
	if (!onConfirm || !onCancel) return null
	return (
		<ConfirmDialog
			open={count !== null}
			title="Replace existing text?"
			description={`${count ?? 0} of the selected cells already have text. Applying AI translation will replace it once the new translations are ready.`}
			confirmLabel="Apply AI translation"
			confirmVariant="danger"
			onConfirm={onConfirm}
			onCancel={onCancel}
		/>
	)
}
